/**
 * DEC5 rules, pure (spec §1 Q6, §2.1, §4, §5). One module so the Settings page,
 * box 6 and the server's box 9/3 targets cannot reach different verdicts.
 * Resolution uses APPROVED rows only (plan ruling R1).
 * Targets are matched on their metric too (DEC3): a target set against Impressions is never resolved for Reach.
 *
 * @module services/targets/targetRules
 */
import type { ResultMetric } from './resultMetric';

export type TargetObjective = 'awareness' | 'consideration' | 'conversion';
export const TARGET_OBJECTIVES: readonly TargetObjective[] = ['awareness', 'consideration', 'conversion'];
export type TargetPlatform = 'facebook' | 'google' | 'tiktok' | 'line';
export const TARGET_PLATFORMS: readonly TargetPlatform[] = ['facebook', 'google', 'tiktok', 'line'];
export type ProposalStatus = 'proposed' | 'approved' | 'rejected' | 'superseded' | 'withdrawn';

interface Decision {
  status: ProposalStatus; proposedBy: string; proposedAt: string; decidedBy: string | null; decidedAt: string | null;
  rejectReason: string | null; overrideReason: string | null;
}
export interface PlanRow extends Decision {
  id: string; workspaceId: string; month: string; currency: string; platform: TargetPlatform | null; amount: number;
}
export interface CprTargetRow extends Decision {
  id: string; workspaceId: string; objective: TargetObjective; metric: ResultMetric; currency: string; platform: TargetPlatform | null;
  target: number; effectiveFrom: string;
}

/** Relative tolerance so 0.9 × expected is not lost to binary floating point. */
const EPS = 1e-9;
const PACING_LOW = 0.9;
const PACING_HIGH = 1.1;
/** Days 1–2 of data are too few to judge a month by. */
const PACING_MIN_DAYS = 3;
export const NEAR_LIMIT = 1.25;

const oneDecimal = (n: number): number => Math.round(n * 10) / 10;
const cents = (n: number): number => Math.round(n * 100) / 100;

export type PacingStatus = 'under' | 'onTrack' | 'over' | 'tooEarly';
export interface Pacing { pct: number | null; status: PacingStatus; projected: number | null }

/** spend ÷ (plan × days ÷ daysInMonth); `days` counts days of DATA, so a lagging sync is not read as underspend. */
export const pacingOf = ({ spend, plan, days, daysInMonth }: { spend: number; plan: number; days: number; daysInMonth: number }): Pacing => {
  // Cents, never whole units: the box shows 2dp, and the CSV and the chat must carry the same number (D2).
  const projected = days > 0 ? cents((spend / days) * daysInMonth) : null;
  if (days < PACING_MIN_DAYS) return { pct: null, status: 'tooEarly', projected };
  const ratio = spend / (plan * (days / daysInMonth));
  const status: PacingStatus = ratio < PACING_LOW * (1 - EPS) ? 'under' : ratio > PACING_HIGH * (1 + EPS) ? 'over' : 'onTrack';
  return { pct: oneDecimal(ratio * 100), status, projected };
};

export type TargetStatus = 'met' | 'near' | 'over25' | 'none' | 'na' | 'failed';

/** Cost per Result against its target; lower is better. */
export const targetVerdict = (costPerResult: number | null, target: number | null): { status: TargetStatus; deltaPct: number | null } => {
  if (target === null) return { status: 'none', deltaPct: null };
  if (costPerResult === null) return { status: 'na', deltaPct: null };
  const ratio = costPerResult / target;
  const deltaPct = oneDecimal((ratio - 1) * 100);
  if (ratio <= 1 + EPS) return { status: 'met', deltaPct };
  if (ratio <= NEAR_LIMIT * (1 + EPS)) return { status: 'near', deltaPct };
  return { status: 'over25', deltaPct };
};

export interface PlanTotal { amount: number; derived: boolean; mismatch: { channelSum: number; total: number } | null }

/**
 * The approved plan for a month and currency. Without `platform`: the total
 * row, else the sum of channel rows (derived). With `platform`: that channel's
 * row. null = no plan, never 0.
 */
export const planFor = (rows: readonly PlanRow[], month: string, currency: string, platform?: TargetPlatform | null): PlanTotal | null => {
  const live = rows.filter((r) => r.status === 'approved' && r.month === month && r.currency === currency);
  if (platform) {
    const row = live.find((r) => r.platform === platform);
    return row ? { amount: row.amount, derived: false, mismatch: null } : null;
  }
  const total = live.find((r) => r.platform === null);
  const channels = live.filter((r) => r.platform !== null);
  const channelSum = cents(channels.reduce((s, r) => s + r.amount, 0));
  if (total) {
    const mismatch = channels.length > 0 && Math.abs(channelSum - total.amount) > 0.005 ? { channelSum, total: total.amount } : null;
    return { amount: total.amount, derived: false, mismatch };
  }
  return channels.length > 0 ? { amount: channelSum, derived: true, mismatch: null } : null;
};

type TargetKey = { objective: TargetObjective; metric: ResultMetric; currency: string; platform: TargetPlatform | null };
const sameKey = (r: CprTargetRow, q: TargetKey): boolean =>
  r.status === 'approved' && r.objective === q.objective && r.metric === q.metric
  && r.currency === q.currency && r.platform === q.platform;

/** The approved target with the latest effective_from ≤ date for exactly this key; null = none in effect. */
export const resolveCprTarget = (rows: readonly CprTargetRow[], q: TargetKey & { date: string }): CprTargetRow | null =>
  rows.filter((r) => sameKey(r, q) && r.effectiveFrom <= q.date)
    .reduce<CprTargetRow | null>((best, r) => (best === null || r.effectiveFrom > best.effectiveFrom ? r : best), null);

/** The latest change of target inside (start, end], as from → to; null = the same target all period. */
export const targetChangeWithin = (rows: readonly CprTargetRow[], q: TargetKey & { start: string; end: string }): { from: number; to: number; on: string } | null => {
  const atStart = resolveCprTarget(rows, { ...q, date: q.start });
  const atEnd = resolveCprTarget(rows, { ...q, date: q.end });
  if (!atEnd || !atStart || atStart.id === atEnd.id) return null;
  return { from: atStart.target, to: atEnd.target, on: atEnd.effectiveFrom };
};

/** DEC3 (R9): approved targets of this objective set against a metric other than `metric` — any currency, platform or date. */
export const otherMetricTargetCount = (rows: readonly CprTargetRow[], objective: TargetObjective, metric: ResultMetric): number =>
  rows.filter((r) => r.status === 'approved' && r.objective === objective && r.metric !== metric).length;
